import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    Button,
    Row,
    Col,
    Modal,
    message,
    Skeleton,
    Icon,
    Progress,
    Tag,
    Dropdown,
    Menu,
    Tooltip,
} from "antd";
import styled from "styled-components";
import moment from "moment";

import { toBRL } from "~/components/UI/InputCurrency";
import { ModuleHeader, ModuleHeaderWrapper } from "~/components/ModuleHeader";
import Search from "~/_shared/components/Search";
import DrawerCreate from "./Components/DrawerCreate";
import * as bancosActions from "./actions";

var PageWrapper = styled.div`
    padding: 24px;
`;

var CardsRow = styled.div`
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
`;

var CardWrapper = styled.div`
    flex: 1;
    min-width: 0;
`;

var SummaryCard = styled.div`
    background: #fff;
    border-radius: 8px;
    padding: 16px 20px;
    border: 1px solid #e8e8e8;
    transition: all 0.2s ease;
    min-height: 100px;

    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    }
`;

var SummaryCardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
`;

var SummaryCardIcon = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${function(props) { return props.bgColor || '#f0f0f0'; }};
    color: ${function(props) { return props.color || '#666'; }};
    font-size: 16px;
`;

var SummaryCardTitle = styled.div`
    font-size: 13px;
    color: rgba(0, 0, 0, 0.45);
    font-weight: 500;
`;

var SummaryCardValue = styled.div`
    font-size: 24px;
    font-weight: 600;
    color: ${function(props) { return props.color || 'rgba(0, 0, 0, 0.85)'; }};
    line-height: 1.2;
`;

var BankCard = styled.div`
    background: #fff;
    border-radius: 8px;
    border: 1px solid #e8e8e8;
    padding: 20px;
    flex: 1;
    transition: all 0.2s ease;

    &:hover {
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
        border-color: #d0d0d0;
    }
`;

var AddBankCard = styled.div`
    background: #fafafa;
    border-radius: 8px;
    border: 2px dashed #d9d9d9;
    padding: 20px;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: rgba(0, 0, 0, 0.25);

    &:hover {
        border-color: #1890ff;
        color: #1890ff;
        background: #e6f7ff;
    }
`;

var BankCardHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 16px;
`;

var BankCardTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
    margin-bottom: 4px;
`;

var BankCardSubtitle = styled.div`
    font-size: 13px;
    color: rgba(0, 0, 0, 0.45);
`;


var StatRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
`;

var StatLabel = styled.span`
    font-size: 13px;
    color: rgba(0, 0, 0, 0.45);
`;

var StatValue = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${function(props) { return props.color || 'rgba(0, 0, 0, 0.85)'; }};
`;

var SaldoAtual = styled.div`
    font-size: 22px;
    font-weight: 700;
    color: ${function(props) { return props.color || 'rgba(0, 0, 0, 0.85)'; }};
    margin: 8px 0 16px;
`;

var EmptyState = styled.div`
    text-align: center;
    padding: 60px 24px;
    color: rgba(0, 0, 0, 0.45);
`;

var BancosList = function() {
    var dispatch = useDispatch();

    var storeState = useSelector(function(state) {
        return {
            data: state.bancos.data,
            isLoading: state.bancos.isLoading,
        };
    });

    var data = storeState.data;
    var isLoading = storeState.isLoading;

    var [showDrawer, setShowDrawer] = useState(false);
    var [editingRecord, setEditingRecord] = useState(null);
    var [search, setSearch] = useState('');

    useEffect(function() {
        dispatch(bancosActions.loadBancos());
    }, []);

    var handleCreate = function() {
        setEditingRecord(null);
        setShowDrawer(true);
    };

    var handleEdit = function(record) {
        setEditingRecord(record);
        setShowDrawer(true);
    };

    var handleDelete = function(record) {
        Modal.confirm({
            title: 'Excluir banco',
            content: 'Tem certeza que deseja excluir o banco "' + record.name + '"? Esta ação não pode ser desfeita.',
            okText: 'Excluir',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: function() {
                return dispatch(bancosActions.deleteBanco(record.id)).then(function(response) {
                    if (response && response.error) {
                        var errMsg = (response.error.response && response.error.response.data && response.error.response.data.message)
                            || 'Erro ao excluir banco';
                        message.error(errMsg);
                    } else {
                        message.success('Banco excluído com sucesso');
                    }
                });
            },
        });
    };

    var handleDrawerClose = function() {
        setShowDrawer(false);
        setEditingRecord(null);
    };

    var handleDrawerSuccess = function() {
        setShowDrawer(false);
        setEditingRecord(null);
        dispatch(bancosActions.loadBancos());
    };

    var handleSearchChange = function(e) {
        setSearch(e.target.value);
    };

    var filteredData = search
        ? data.filter(function(item) {
            var searchLower = search.toLowerCase();
            var name = (item.name || '').toLowerCase();
            var bankCode = (item.bank_code || '').toLowerCase();
            var agency = (item.agency || '').toLowerCase();
            var accountNumber = (item.account_number || '').toLowerCase();
            return name.indexOf(searchLower) > -1
                || bankCode.indexOf(searchLower) > -1
                || agency.indexOf(searchLower) > -1
                || accountNumber.indexOf(searchLower) > -1;
        })
        : data;

    var totalSaldoAtual = filteredData.reduce(function(sum, item) {
        return sum + (item.saldo_atual || 0);
    }, 0);

    var totalCredits = filteredData.reduce(function(sum, item) {
        return sum + (item.total_credits || 0);
    }, 0);

    var totalDebits = filteredData.reduce(function(sum, item) {
        return sum + (item.total_debits || 0);
    }, 0);

    var getBankSubtitle = function(record) {
        var parts = [];
        if (record.agency) {
            parts.push('Ag. ' + record.agency);
        }
        if (record.account_number) {
            parts.push('Conta ' + record.account_number);
        }
        return parts.join(' · ');
    };

    return (
        <div>
            <ModuleHeaderWrapper>
                <ModuleHeader
                    breadcrumbs={["Financeiro", "Bancos"]}
                    title="Bancos"
                    search={
                        <Search
                            value={search}
                            placeholder="Pesquisar banco..."
                            onChange={handleSearchChange}
                        />
                    }
                    center={
                        <Dropdown
                            trigger={['click']}
                            overlay={
                                <Menu>
                                    <Menu.Item key="new" onClick={handleCreate}>
                                        <Icon type="plus" /> Novo Banco
                                    </Menu.Item>
                                </Menu>
                            }
                        >
                            <Button icon="plus" shape="circle" />
                        </Dropdown>
                    }
                />
            </ModuleHeaderWrapper>

            <PageWrapper>
                <CardsRow>
                    <CardWrapper>
                        <SummaryCard>
                            {isLoading ? (
                                <Skeleton active paragraph={{ rows: 1 }} title={{ width: 100 }} />
                            ) : (
                                <div>
                                    <SummaryCardHeader>
                                        <SummaryCardIcon bgColor="#e6f7ff" color="#1890ff">
                                            <Icon type="bank" />
                                        </SummaryCardIcon>
                                        <SummaryCardTitle>Saldo Total</SummaryCardTitle>
                                    </SummaryCardHeader>
                                    <SummaryCardValue color={totalSaldoAtual >= 0 ? '#52c41a' : '#f5222d'}>
                                        {toBRL(totalSaldoAtual)}
                                    </SummaryCardValue>
                                </div>
                            )}
                        </SummaryCard>
                    </CardWrapper>
                    <CardWrapper>
                        <SummaryCard>
                            {isLoading ? (
                                <Skeleton active paragraph={{ rows: 1 }} title={{ width: 100 }} />
                            ) : (
                                <div>
                                    <SummaryCardHeader>
                                        <SummaryCardIcon bgColor="#f6ffed" color="#52c41a">
                                            <Icon type="arrow-up" />
                                        </SummaryCardIcon>
                                        <SummaryCardTitle>Total Créditos</SummaryCardTitle>
                                    </SummaryCardHeader>
                                    <SummaryCardValue color="#52c41a">
                                        {toBRL(totalCredits)}
                                    </SummaryCardValue>
                                </div>
                            )}
                        </SummaryCard>
                    </CardWrapper>
                    <CardWrapper>
                        <SummaryCard>
                            {isLoading ? (
                                <Skeleton active paragraph={{ rows: 1 }} title={{ width: 100 }} />
                            ) : (
                                <div>
                                    <SummaryCardHeader>
                                        <SummaryCardIcon bgColor="#fff1f0" color="#f5222d">
                                            <Icon type="arrow-down" />
                                        </SummaryCardIcon>
                                        <SummaryCardTitle>Total Débitos</SummaryCardTitle>
                                    </SummaryCardHeader>
                                    <SummaryCardValue color="#f5222d">
                                        {toBRL(totalDebits)}
                                    </SummaryCardValue>
                                </div>
                            )}
                        </SummaryCard>
                    </CardWrapper>
                    <CardWrapper>
                        <SummaryCard>
                            {isLoading ? (
                                <Skeleton active paragraph={{ rows: 1 }} title={{ width: 100 }} />
                            ) : (
                                <div>
                                    <SummaryCardHeader>
                                        <SummaryCardIcon bgColor="#f9f0ff" color="#722ed1">
                                            <Icon type="credit-card" />
                                        </SummaryCardIcon>
                                        <SummaryCardTitle>Bancos Cadastrados</SummaryCardTitle>
                                    </SummaryCardHeader>
                                    <SummaryCardValue>
                                        {filteredData.length}
                                    </SummaryCardValue>
                                </div>
                            )}
                        </SummaryCard>
                    </CardWrapper>
                </CardsRow>

                {isLoading ? (
                    <Row gutter={16}>
                        {[1, 2, 3].map(function(i) {
                            return (
                                <Col key={i} xs={24} sm={12} lg={8}>
                                    <BankCard>
                                        <Skeleton active paragraph={{ rows: 4 }} />
                                    </BankCard>
                                </Col>
                            );
                        })}
                    </Row>
                ) : data.length === 0 ? (
                    <EmptyState>
                        <Icon type="bank" style={{ fontSize: 48, marginBottom: 16, display: 'block' }} />
                        <div style={{ fontSize: 16, marginBottom: 8 }}>Nenhum banco cadastrado</div>
                        <div style={{ marginBottom: 24 }}>Adicione sua primeira conta bancária para começar</div>
                        <Button type="primary" icon="plus" onClick={handleCreate}>
                            Novo Banco
                        </Button>
                    </EmptyState>
                ) : (
                    <Row gutter={[16, 16]} type="flex">
                        {filteredData.map(function(record) {
                            var saldoColor = record.saldo_atual >= 0 ? '#52c41a' : '#f5222d';
                            var subtitle = getBankSubtitle(record);

                            var cardMenu = (
                                <Menu>
                                    <Menu.Item key="edit" onClick={function() { handleEdit(record); }}>
                                        <Icon type="edit" /> Editar
                                    </Menu.Item>
                                    <Menu.Divider />
                                    <Menu.Item key="delete" onClick={function() { handleDelete(record); }} style={{ color: '#f5222d' }}>
                                        <Icon type="delete" /> Excluir
                                    </Menu.Item>
                                </Menu>
                            );

                            return (
                                <Col key={record.id} xs={24} sm={12} lg={8} style={{ display: 'flex', flexDirection: 'column' }}>
                                    <BankCard>
                                        <BankCardHeader>
                                            <div>
                                                <BankCardTitle>
                                                    {record.name}
                                                    {record.bank_code && (
                                                        <Tag
                                                            color="blue"
                                                            style={{ marginLeft: 8, fontSize: 11 }}
                                                        >
                                                            {record.bank_code}
                                                        </Tag>
                                                    )}
                                                </BankCardTitle>
                                                {subtitle ? (
                                                    <BankCardSubtitle>{subtitle}</BankCardSubtitle>
                                                ) : null}
                                            </div>
                                            <Dropdown overlay={cardMenu} placement="bottomRight" trigger={['click']}>
                                                <Button size="small" icon="more" />
                                            </Dropdown>
                                        </BankCardHeader>

                                        <SaldoAtual color={saldoColor}>
                                            {toBRL(record.saldo_atual || 0)}
                                        </SaldoAtual>

                                        <StatRow>
                                            <StatLabel>Saldo Inicial</StatLabel>
                                            <StatValue>{toBRL(record.saldo_inicial || 0)}</StatValue>
                                        </StatRow>
                                        <StatRow>
                                            <StatLabel>Total Créditos</StatLabel>
                                            <StatValue color="#52c41a">{toBRL(record.total_credits || 0)}</StatValue>
                                        </StatRow>
                                        <StatRow>
                                            <StatLabel>Total Débitos</StatLabel>
                                            <StatValue color="#f5222d">{toBRL(record.total_debits || 0)}</StatValue>
                                        </StatRow>

                                        {record.pending_count > 0 && (
                                        <Tooltip title={record.pending_count + ' lançamento(s) aguardando conciliação. Acesse a Conciliação Bancária para resolver.'}>
                                            <StatRow style={{ marginTop: 8, cursor: 'help' }}>
                                                <StatLabel style={{ color: '#fa8c16' }}>
                                                    <Icon type="exclamation-circle" style={{ marginRight: 4 }} />
                                                    Pendentes
                                                </StatLabel>
                                                <StatValue color="#fa8c16">{record.pending_count}</StatValue>
                                            </StatRow>
                                        </Tooltip>
                                        )}

                                        <div style={{ marginTop: 12 }}>
                                            <StatRow style={{ marginBottom: 4 }}>
                                                <StatLabel>Conciliado</StatLabel>
                                                <StatLabel>{record.reconciliation_progress || 0}%</StatLabel>
                                            </StatRow>
                                            <Progress
                                                percent={record.reconciliation_progress || 0}
                                                showInfo={false}
                                                strokeColor="#52c41a"
                                                size="small"
                                            />
                                        </div>

                                        <div style={{ marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
                                            <StatLabel>
                                                <Icon type="clock-circle" style={{ marginRight: 4 }} />
                                                {record.last_reconciled_at
                                                    ? 'Última conciliação ' + moment(record.last_reconciled_at).fromNow()
                                                    : 'Nunca conciliado'}
                                            </StatLabel>
                                        </div>
                                    </BankCard>
                                </Col>
                            );
                        })}
                        <Col xs={24} sm={12} lg={8} style={{ display: 'flex', flexDirection: 'column' }}>
                            <AddBankCard onClick={handleCreate}>
                                <Icon type="plus-circle" style={{ fontSize: 32, marginBottom: 12 }} />
                                <div style={{ fontSize: 14, fontWeight: 500 }}>Adicionar banco</div>
                            </AddBankCard>
                        </Col>
                    </Row>
                )}
            </PageWrapper>

            <DrawerCreate
                visible={showDrawer}
                onClose={handleDrawerClose}
                onSuccess={handleDrawerSuccess}
                record={editingRecord}
            />
        </div>
    );
};

export default BancosList;
